import { useState } from 'react';

import { CalendarClock, ExternalLink, Loader2, Send, UserRoundX } from 'lucide-react';

import SideDrawer from '../../shared/SideDrawer';
import Chip from '../crm.chip';
import CrmTriageBriefingCard from '../opportunity/briefing/crm.triage.briefing.card';
import ScheduleModal from '../opportunity/schedule/crm.opportunity.schedule.modal';

import masks from '../../../providers/masks';

import useOrbitaConversation, { conversationStatuses } from '../../../hooks/crm/useOrbitaConversation';
import useOrbitaContactHistory, { historyStatuses } from '../../../hooks/crm/useOrbitaContactHistory';
import useOpportunityScheduleFacts from '../../../hooks/crm/useOpportunityScheduleFacts';

import { chipTones, getContactTypeLabel, getPipelineChip } from '../../../domain/constant/crm/crm.chip.constant';
import { DRAWER_CONTENT_DELAY_MS, triageBriefingSurfaces } from '../../../domain/constant/crm/crm.triage.briefing.constant';
import { scheduleTexts } from '../../../domain/constant/crm/opportunity/crm.opportunity.schedule.constant';
import {
    conversationHistoryTexts,
    conversationTexts,
    conversationViews,
} from '../../../domain/constant/crm/crm.conversation.constant';
import crmOpportunityScheduleService from '../../../domain/services/crm/crm.opportunity.schedule.service';

const getInitials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('') || '?';

const MessageDrawer = ({
    contact = {},
    opportunity = {},
    activity = null,
    scheduleOrigin = null,
    initialView = conversationViews.conversation,
    onSent,
    onScheduled,
    onSellerChanged,
    onExit,
}) => {

    const [isOpen, setIsOpen] = useState(true);
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [view, setView] = useState(initialView);

    const exit = () => setIsOpen(false);

    const activityId = activity?.opportunityActivityId || activity?.id || null;

    const { opportunity: scheduleOpportunity, refresh: refreshScheduleFacts } = useOpportunityScheduleFacts(opportunity);

    const handleSent = (sentActivity) => {
        refreshScheduleFacts('sent');
        onSent && onSent(sentActivity);
    };

    const conversation = useOrbitaConversation(opportunity.id, {
        isOpen: isOpen && view === conversationViews.conversation,
        onSent: handleSent,
        onReleased: exit,
        activityId,
    });

    const contactHistory = useOrbitaContactHistory(opportunity.id, {
        isOpen: isOpen && initialView === conversationViews.history,
    });

    const isOpeningConversation = view === conversationViews.conversation
        && conversation.status === conversationStatuses.loading
        && !conversation.embedSrc;

    const isShowingHistory = initialView === conversationViews.history
        && (view === conversationViews.history || isOpeningConversation);

    const canScheduleFromDrawer = crmOpportunityScheduleService.canScheduleFromDrawer(scheduleOpportunity, activity);

    const handleScheduled = (result) => {
        setIsScheduleOpen(false);
        exit();
        onScheduled && onScheduled(result);
    };

    const refreshOpportunity = () => {
        exit();
        onSellerChanged && onSellerChanged();
    };

    const [exitFired, setExitFired] = useState(false);

    const fireExit = () => {
        if (exitFired)
            return;

        setExitFired(true);
        onExit();
    };

    const getDismissHandler = () => conversation.hasDraft ? null : exit;

    const getChips = () => {
        const contactTypeLabel = getContactTypeLabel(contact);
        const pipelineChip = getPipelineChip(opportunity.pipelineName);

        return [
            contactTypeLabel && { key: contactTypeLabel, label: contactTypeLabel, tone: chipTones.outline },
            pipelineChip && { key: pipelineChip.label, ...pipelineChip },
            opportunity.statusName && { key: opportunity.statusName, label: opportunity.statusName, tone: chipTones.neutral },
        ].filter(Boolean);
    };

    const getObjective = () => activity?.isContactResponse
        ? null
        : opportunity.objective || activity?.description;

    const renderHeader = () => {
        const chips = getChips();
        const objective = getObjective();

        return (
            <div>
                <div className="flex items-center gap-2.5">
                    <div className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-primaryTint text-[12.5px] font-semibold text-primaryDark">
                        {getInitials(contact.name)}
                    </div>

                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                        <p className="truncate text-[14px] font-semibold text-gray-900">{contact.name}</p>
                        <p className="text-[12.5px] tabular-nums text-gray-500">
                            {contact.phone ? masks.cellPhone(contact.phone) : null}
                        </p>
                    </div>
                </div>

                {!!chips.length && (
                    <div className="mt-2 flex flex-wrap gap-[6px]">
                        {chips.map(chip => (
                            <Chip key={chip.key} tone={chip.tone}>{chip.label}</Chip>
                        ))}
                    </div>
                )}

                {objective && (
                    <p className="mt-2 rounded-md bg-blue-50/70 px-2.5 py-1.5 text-[12.5px] leading-snug text-gray-700">
                        <span className="font-semibold uppercase tracking-[0.05em] text-blue-700">Objetivo</span>
                        <span className="mx-1.5 text-blue-300">·</span>
                        {objective}
                    </p>
                )}

                <CrmTriageBriefingCard
                    briefing={scheduleOpportunity.triageBriefing}
                    opportunityId={opportunity.id}
                    surface={triageBriefingSurfaces.message}
                    className="mt-2"
                    panelClassName="max-h-[40vh] overflow-y-auto"
                    enterDelayMs={DRAWER_CONTENT_DELAY_MS}
                />
            </div>
        );
    };

    const renderMessage = (content) => (
        <div className="flex h-full items-center justify-center px-6 text-center">
            {content}
        </div>
    );

    const renderLoading = (text, { isSlow = false } = {}) => (
        <div role="status" className="flex h-full flex-col items-center justify-center gap-2 bg-white px-6 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-[13.5px] text-gray-600">{text}</p>
            {isSlow && (
                <p className="max-w-[320px] text-[12.5px] text-gray-500">{conversationTexts.slow}</p>
            )}
        </div>
    );

    const renderSellerChanged = (message) => renderMessage(
        <div role="status" className="flex max-w-[380px] flex-col items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-[13.5px] text-amber-900">
            <UserRoundX className="h-5 w-5 text-amber-600" />
            <p>{message}</p>
            <button
                type="button"
                className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-900 hover:bg-amber-100"
                onClick={refreshOpportunity}
            >
                {conversationTexts.refreshOpportunity}
            </button>
        </div>
    );

    const renderFailure = (message, retry) => renderMessage(
        <div>
            <p className="text-[13.5px] text-gray-600">{message}</p>
            <button
                type="button"
                className="mt-2 text-[13px] font-medium text-primary hover:underline"
                onClick={() => retry()}
            >
                Tentar de novo
            </button>
        </div>
    );

    const renderEmbed = ({ frameRef, title, src, allow, overlay }) => (
        <div className="relative h-full">
            <iframe
                ref={frameRef}
                title={title}
                src={src}
                allow={allow}
                className="h-full w-full border-0"
            />

            {overlay && (
                <div className="absolute inset-0">
                    {overlay}
                </div>
            )}
        </div>
    );

    const renderConversation = () => {

        if (conversation.status === conversationStatuses.sellerChanged)
            return renderSellerChanged(conversation.errorMessage);

        if (conversation.status === conversationStatuses.error)
            return renderFailure(conversation.errorMessage, conversation.reload);

        if (conversation.status === conversationStatuses.unavailable)
            return renderMessage(
                <p className="text-[13.5px] text-gray-600">
                    A conversa não pode ser aberta aqui. Use o link do Órbita abaixo.
                </p>
            );

        if (!conversation.embedSrc)
            return renderLoading(conversationTexts.opening, { isSlow: conversation.isSlow });

        return renderEmbed({
            frameRef: conversation.frameRef,
            title: 'Conversa do Órbita',
            src: conversation.embedSrc,
            allow: 'microphone',
            overlay: conversation.status !== conversationStatuses.ready
                ? renderLoading(conversationTexts.loadingEmbed, { isSlow: conversation.isSlow })
                : null,
        });
    };

    const renderHistory = () => {

        if (contactHistory.status === historyStatuses.sellerChanged)
            return renderSellerChanged(contactHistory.errorMessage);

        if (contactHistory.status === historyStatuses.error)
            return renderFailure(contactHistory.errorMessage, contactHistory.reload);

        if (contactHistory.status === historyStatuses.empty)
            return renderMessage(
                <p className="text-[13.5px] text-gray-600">{conversationHistoryTexts.empty}</p>
            );

        if (!contactHistory.embedSrc)
            return renderLoading(conversationHistoryTexts.loading);

        return renderEmbed({
            frameRef: contactHistory.frameRef,
            title: 'Histórico do Órbita',
            src: contactHistory.embedSrc,
            overlay: contactHistory.status !== historyStatuses.ready
                ? renderLoading(conversationHistoryTexts.loading)
                : null,
        });
    };

    const renderOrbitaLink = () => !isShowingHistory && conversation.conversationUrl
        ? (
            <a
                className="flex items-center justify-center gap-1.5 text-[13px] font-medium text-gray-500 hover:text-primary"
                href={conversation.conversationUrl}
                target="_blank"
                rel="noreferrer"
            >
                <ExternalLink className="h-3.5 w-3.5" />
                Abrir no Órbita
            </a>
        )
        : null;

    const renderScheduleAction = () => canScheduleFromDrawer || isScheduleOpen
        ? (
            <>
                {canScheduleFromDrawer && (
                    <button
                        type="button"
                        className="flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsScheduleOpen(true)}
                    >
                        <CalendarClock className="h-3.5 w-3.5" />
                        {scheduleTexts.drawerActionLabel}
                    </button>
                )}

                <ScheduleModal
                    isOpen={isScheduleOpen}
                    opportunity={opportunity}
                    activityId={activityId}
                    origin={scheduleOrigin}
                    onClose={() => setIsScheduleOpen(false)}
                    onScheduled={handleScheduled}
                />
            </>
        )
        : null;

    const renderSendMessageAction = () => {

        if (!isShowingHistory || contactHistory.status === historyStatuses.sellerChanged)
            return null;

        return (
            <div className="flex w-full flex-col items-center gap-1.5">
                <button
                    type="button"
                    disabled={isOpeningConversation}
                    className="flex h-[46px] w-full items-center justify-center gap-2 rounded bg-primary text-[15px] font-semibold text-white transition-colors hover:bg-primaryDark disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => setView(conversationViews.conversation)}
                >
                    {isOpeningConversation
                        ? <Loader2 className="h-[18px] w-[18px] animate-spin" />
                        : <Send className="h-[18px] w-[18px]" />}
                    {isOpeningConversation
                        ? conversationHistoryTexts.openingConversation
                        : conversationHistoryTexts.sendMessage}
                </button>

                {isOpeningConversation && conversation.isSlow && (
                    <p className="max-w-[320px] text-center text-[12px] text-gray-500">{conversationTexts.slow}</p>
                )}
            </div>
        );
    };

    const renderFooter = () => {
        const scheduleAction = renderScheduleAction();
        const orbitaLink = renderOrbitaLink();
        const sendMessageAction = renderSendMessageAction();

        if (!scheduleAction && !orbitaLink && !sendMessageAction)
            return null;

        return (
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                {scheduleAction}
                {orbitaLink}
                {sendMessageAction}
            </div>
        );
    };

    return (
        <SideDrawer
            isOpen={isOpen}
            onDismiss={getDismissHandler()}
            onCloseButtonClick={exit}
            afterLeave={fireExit}
            title="Conversa"
            widthClassName="max-w-[560px]"
            contentClassName="flex-1 overflow-hidden"
            header={renderHeader()}
            footer={renderFooter()}
        >
            {isShowingHistory ? renderHistory() : renderConversation()}
        </SideDrawer>
    );
};

export default MessageDrawer;
